import { useQuery } from '@tanstack/react-query';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { DashboardPractice, fetchMyPractices } from '../../services/practices';
import { useUser } from '../UserProvider';

export type PracticeType = DashboardPractice;

interface PracticeContextType {
    practices: DashboardPractice[];
    isLoading: boolean;
    error: string | null;
    loadPractices: (options?: { refresh?: boolean }) => Promise<void>;
    reset: () => void;
}

const Ctx = createContext<PracticeContextType | undefined>(undefined);

export function usePractice(): PracticeContextType {
    const context = useContext(Ctx);
    if (context === undefined) {
        throw new Error('usePractice must be used within a PracticesProvider');
    }
    return context;
}

export function PracticesProvider({ children }: { children: ReactNode }) {
    const { user } = useUser();
    const practiceIds = user?.database.data.practices;

    const {
        data: practices = [],
        isLoading,
        error: queryError,
        refetch
    } = useQuery({
        queryKey: ['practices', 'mine', practiceIds],
        queryFn: fetchMyPractices,
        enabled: !!practiceIds?.length,
        staleTime: 15 * 60 * 1000
    });

    const error = queryError
        ? queryError instanceof Error
            ? queryError.message
            : 'Failed to fetch practices'
        : null;

    const loadPractices = useCallback(async () => {
        await refetch();
    }, [refetch]);

    const reset = useCallback(() => {
        refetch();
    }, [refetch]);

    const value = useMemo<PracticeContextType>(
        () => ({ practices, isLoading, error, loadPractices, reset }),
        [practices, isLoading, error, loadPractices, reset]
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
